/**
 * Every photo and video slot on the facial balancing / liquid facelift ad pages, in ONE map.
 * To swap one: drop the new files in client/public/images (or /videos) and change the slot here.
 * Real Lakewood patients and clinic footage only — never stock, never generated faces, never
 * the desk/stethoscope headshot (owner ruling 2026-10-07).
 *
 * v9 (2026-10-08, owner: "lead this page with me doing the procedure on that girl with
 * face/chin"): the hero is a muted, looping video of Dr. Kohli treating the chin of the consented
 * denim / brown-tank patient. This overrides the earlier "no needle above the fold" rule for the
 * HERO ONLY, by the owner's explicit call. The link preview (shared/routeOgImages.ts) stays
 * needle-free. She had NO jawline filler: never caption her with the jawline.
 *
 * Hero clip: iPhone HLG HDR originals, tone-mapped to SDR (zscale linear npl=600 -> hable ->
 * bt709, Remotion compositor ffmpeg), cropped 4:5 to keep his face and her chin:
 *   IMG_0181.MOV 26.8–30.8 s, then IMG_0197.MOV 21.3–27.6 s (0.4 s dissolve between them),
 *   made seamless by dissolving the last 0.5 s back into the first frame. 9.4 s, no audio track.
 * The poster is that first frame, served as the LCP image (preloaded by the server); the video
 * only starts after window load and never under prefers-reduced-motion.
 *
 * The compare sliders use before/after pairs aligned on the pupils, so the divider shows the
 * change and nothing else moves.
 */
import { FACIAL_BALANCING_HERO_IMAGE } from "@shared/routeImagePreloads";

export type LpImage = { src: string; srcSet: string; width: number; height: number };
export type LpCompare = {
  key: string;
  caption: string;
  before: LpImage & { alt: string };
  after: LpImage & { alt: string };
  /** What the divider reveals, for the range input's accessible name. */
  label: string;
};

const webp = (base: string, widths: number[]) => ({
  src: `${base}-${widths[widths.length - 1]}.webp`,
  srcSet: widths.map(w => `${base}-${w}.webp ${w}w`).join(", "),
});

const pair = (base: string) => ({ ...webp(base, [720, 1080]), width: 1080, height: 1350 });

export const HERO_VIDEO = {
  /** Poster = the loop's first frame. LCP image; same srcset + sizes as the server preload. */
  poster: FACIAL_BALANCING_HERO_IMAGE,
  mp4: "/videos/facial-balancing-hero-dr-kohli-chin.mp4",
  webm: "/videos/facial-balancing-hero-dr-kohli-chin.webm",
  width: 720,
  height: 900,
} as const;

export const LP_IMAGES = {
  hero: FACIAL_BALANCING_HERO_IMAGE,
  /** Results carousel (#results). LP only — before/afters never go into Meta ads. */
  compare: [
    {
      // Owner 2026-10-10: replaces the denim front pair ("not flattering") and leads the carousel.
      // The platinum "face of the franchise" patient's own Instagram before/after (IG DYlHVISp5_8 carousel,
      // slide 5): PRF under-eye, 3 months. Split from his card and aligned on the pupils; no retouch.
      // PRF is her own platelet-rich fibrin; the owner calls it under-eye filler, so say PRF every time.
      key: "platinum-prf",
      caption: "Under-eye filler with PRF, 3 months",
      label: "PRF under-eye filler",
      before: {
        ...pair("/images/facial-balancing-cmp-platinum-prf-before"),
        alt: "Before PRF under-eye filler at Lakewood Med Spa: a patient photographed from the front with under-eye shadows",
      },
      after: {
        ...pair("/images/facial-balancing-cmp-platinum-prf-after"),
        alt: "After PRF under-eye filler at Lakewood Med Spa: the same patient from the front 3 months after PRF made from her own blood",
      },
    },
    {
      key: "pinktop-front",
      caption: "Lips and lower face, front view",
      label: "facial balancing, front view",
      before: {
        ...pair("/images/facial-balancing-cmp-pinktop-front-before"),
        alt: "Before facial balancing at Lakewood Med Spa: a patient photographed from the front before treatment",
      },
      after: {
        ...pair("/images/facial-balancing-cmp-pinktop-front-after"),
        alt: "After facial balancing at Lakewood Med Spa: the same patient from the front the same afternoon, after lip and lower-face filler",
      },
    },
    {
      key: "pinktop-profile",
      caption: "The same patient, in profile",
      label: "facial balancing, profile view",
      before: {
        ...pair("/images/facial-balancing-cmp-pinktop-profile-before"),
        alt: "Before facial balancing at Lakewood Med Spa: the same patient in right profile before treatment",
      },
      after: {
        ...pair("/images/facial-balancing-cmp-pinktop-profile-after"),
        alt: "After facial balancing at Lakewood Med Spa: the same patient in right profile, showing lip and chin projection after treatment",
      },
    },
    {
      key: "lips-yellow",
      caption: "Lip filler, after healing",
      label: "lip filler",
      before: {
        ...pair("/images/facial-balancing-cmp-lips-yellow-before"),
        alt: "Before lip filler at Lakewood Med Spa: a patient photographed from the front before treatment",
      },
      after: {
        ...pair("/images/facial-balancing-cmp-lips-yellow-after"),
        alt: "After lip filler at Lakewood Med Spa: the same patient photographed from the front after healing",
      },
    },
  ] as LpCompare[],
} as const;


/** The owner's two Instagram reels, one woman per reel (owner 2026-10-10). */
export const LP_REELS = [
  {
    key: "blonde",
    title: "Facial balancing",
    note: "Chin and midface, performed by Dr. Kohli",
    poster: "/images/fb-lp-reel-blonde.webp",
    mp4: "/videos/fb-lp-reel-blonde-b.mp4",
    webm: "/videos/fb-lp-reel-blonde-b.webm",
    alt: "Real Lakewood Med Spa patient receiving facial balancing from Dr. Rajan Kohli: before and after, chin and midface",
  },
  {
    key: "platinum",
    title: "Under-eye rejuvenation",
    note: "PRF under-eye filler, made from your own blood",
    poster: "/images/fb-lp-reel-platinum.webp",
    mp4: "/videos/fb-lp-reel-platinum.mp4",
    webm: "/videos/fb-lp-reel-platinum.webm",
    alt: "Real Lakewood Med Spa patient receiving PRF under-eye rejuvenation",
  },
] as const;
